/* ══════════════════════════════════════════════════════════════════════
   Skön Cosmetics — skon-identity.js
   asset: assets/skon-identity.js
   Aggiornato: 27 ottobre 2026, ore 01:15 (Europe/Rome)
   Versione: 2.4.0

   ── Modifiche v2.4.0 (27 ott 2026, ore 01:15 Europe/Rome) ──────────────
   - AGGIUNTO: un logout pulisce l'identità del visitatore, invece di
     lasciarla in cache. Richiesta esplicita di Matteo dopo aver scoperto
     (test del 26 ott 2026) che su un dispositivo/tab condiviso un logout
     NON cancellava `skon_identity_state` né il cookie `skon_vid`: il
     visitatore successivo sullo stesso browser veniva riconosciuto come
     il cliente appena uscito (toast in modalità auto-consent, nome e
     preferenze del cliente precedente, stesso visitor_id di tracciamento
     eventi).
   - Non esisteva prima nessun aggancio per il logout: `/account/logout`
     è una navigazione a pagina intera di Shopify, non un evento JS.
     Rilevamento SENZA aggiungere alcun hook nuovo: a ogni bootstrap, se
     `window.SKON_CUSTOMER.loggedIn === false` (verità server-side,
     iniettata da Liquid — vedi sotto) MA `sessionStorage.skon_rc_verified`
     vale ancora '1' (scritto da markCustomer() all'ultima pagina vista
     da loggato, nello stesso tab), allora QUESTO tab risultava
     autenticato e ora non lo è più: è un logout avvenuto nel frattempo.
     Guardia scelta apposta perché `skon_rc_verified` ha UN SOLO
     scrittore in tutto il file (markCustomer()): nessun falso positivo
     possibile per un visitatore mai stato loggato.
   - Alla rilevazione, `_clearOnLogout()` (nuova funzione) ripulisce:
     (a) il modello v2 — `_state` in memoria, `skon_identity_state` sia in
     sessionStorage sia in localStorage; (b) la memoria di sessione della
     cascata legacy che altrimenti sopravvivrebbe alla navigazione di
     logout nello stesso tab (`skon_rc_verified`, `skon_visitor_type`,
     `skon_nc_declared`, `skon_nc_audience_raw` — senza pulire anche
     queste, getVisitorType() avrebbe comunque riconosciuto RC il
     visitatore successivo tramite i Livelli 1/4/5, vanificando la
     pulizia del solo modello v2); (c) il cookie `skon_vid`
     (`skon-fs-onboarding.js` e `skon-commerce-events.js` ne
     leggono/rigenerano uno nuovo alla prima chiamata successiva della
     loro `_ensureVisitorId()`, perché entrambi usano esattamente lo
     stesso schema cookie di questo file — nome, `path=/`,
     `SameSite=Lax` — nessuna dipendenza nuova introdotta: solo
     invalidazione di un cookie che altri file rigenerano da soli).
   - NON toccato di proposito: i cookie `skon_customer`/`skon_nc_verified`
     (365gg, Livelli 1b/2c). Sono l'ACQUISTO ACCERTATO dal Worker, non una
     sessione autenticata — un compromesso già accettato con Matteo il
     10 lug 2026 (vedi changelog v2.2.0), per cui quel segnale sopravvive
     di proposito fino a 365 giorni anche su dispositivo condiviso.
     Rimuoverlo qui avrebbe ribaltato quella decisione senza che nessuno
     l'avesse chiesto: resta fuori dal perimetro di questa modifica.
   - Nessuna modifica a getVisitorType(), CONFIDENCE, observe(),
     markCustomer(), markNonCustomer(): additivo, stesso principio delle
     versioni precedenti. ATTENZIONE a cosa significa in pratica, dato che
     getVisitorType() resta invariato: se il cookie skon_customer/
     skon_nc_verified (365gg) è presente, lo STESSO bootstrap che chiama
     _clearOnLogout() richiama poi getVisitorType() più in basso, che
     rilegge quel cookie (Livello 1b/2c, invariato) e ri-deriva RC/NC —
     ESATTAMENTE come oggi, perché quel cookie è voluto e non lo tocchiamo.
     Il risultato osservabile del fix NON è quindi "il prossimo visitatore
     diventa sempre 'unknown'", ma "il prossimo visitatore sullo stesso
     dispositivo NON eredita più il profilo/nome/sconto/flag-ordine né il
     visitor_id di tracciamento del cliente uscito". Solo quando NON c'è
     alcun cookie 365gg a ri-derivare (es. un RC segnalato solo via
     sessionStorage) il risultato torna realmente 'unknown'. Verificato con
     scenario di test isolato che copre entrambi i casi, oltre a
     observe()/markCustomer()/markNonCustomer()/hasCompletedQuiz()
     invariati: nessuna regressione sui casi esistenti.
   - `version` bumpata a 2.4.0 (stessa guardia `_verLt` già presente dalla
     v2.2.0).

   ── Modifiche v2.3.0 (9 ott 2026) ──────────────────────────────────────
   - AGGIUNTA voce 'email_captured' (conf. 60) alla tabella CONFIDENCE.
     Nuovo touchpoint: il modulo lead-gen NC dello step 2 del toast
     onboarding (assets/skon-fs-onboarding.js v3.0.0) chiama
     SkonID.observe('email_captured', {type:'NC'}) dopo un invio email
     riuscito. Prima quel segnale andava perso: un visitatore che aveva
     volontariamente dato la propria email restava con la sola confidenza
     'declared' (50, autodichiarazione), indistinguibile da chi aveva solo
     cliccato un bottone. 60 è intenzionalmente tra 'declared' (50) e
     'quiz'/'order' (80/90): un'email data volontariamente è un segnale più
     forte di un click, ma più debole di un acquisto verificato o di un
     quiz completato e controllato sugli ordini. Nessuna logica esistente
     cambia: `observe()` e `getVisitorType()` sono invariati, è una riga
     sola nella tabella (vedi sezione ESTENDERE in fondo al file, che
     descrive esattamente questo tipo di modifica).
   - `version` bumpata a 2.3.0 (stesso motivo della guardia `_verLt` già
     presente dalla v2.2.0: senza, un vecchio SkonID già in pagina
     vincerebbe sul nuovo).

   ── Modifiche v2.2.0 (10 lug 2026) — deploy ATOMICO con skon-quiz.js,
      nc-experience.js, skon-fs-test.js, skon-quiz-priority.js ──────────────
   - RIMOSSO il "Livello 2b — Quiz già completato": classificava RC un lead che
     aveva solo compilato il quiz. RC significa "ha comprato". Vedi il commento
     nella cascata per l'analisi completa e l'impatto misurato in produzione.
   - AGGIUNTO "Livello 1b" — cookie `skon_customer` (365gg), scritto SOLO da
     markCustomer() dopo verifica dell'email sugli ordini Shopify.
     Effetto netto: il riconoscimento dei clienti MIGLIORA. Prima, un cliente
     che faceva il quiz veniva riconosciuto solo grazie al cookie del quiz (60gg,
     per la ragione sbagliata); ora è riconosciuto perché ha davvero comprato.
   - NUOVE API pubbliche: hasCompletedQuiz(), markCustomer().
   - `version` bumpata a 2.2.0 ANCHE nella guardia `_verLt` dell'export: senza,
     un vecchio SkonID già presente in pagina avrebbe vinto sul nuovo.
   - CERTIFICAZIONE SERVER-SIDE in _bootstrap: legge window.SKON_CUSTOMER
     (iniettata da Liquid con customer.orders_count) e certifica subito, senza
     rete. Prima: SKON_CUSTOMER non era definita da nessuno e il cookie
     `_shopify_customer_token` (livello 2) non esiste su questo tema — un
     cliente loggato risultava 'unknown'. Ora: orders_count>0 → RC immediato;
     orders_count===0 → NC accertato (si può avere un account senza aver mai
     comprato).

   SkonID — sistema di identità visitatori Skön
   ──────────────────────────────────────────────────────────────────────
   Fonte di verità UNICA per la classificazione del visitatore (NC/RC).

   ┌─ COMPATIBILITÀ (LEGGERE PRIMA DI TOCCARE) ───────────────────────────┐
   │ getVisitorType() è INVARIATO rispetto alla v1.0.0: stessa cascata,   │
   │ stessi input, stessi output, stessi side-effect su sessionStorage.   │
   │ I consumer esistenti (nc-experience._classify, skon-fs-test          │
   │ SkonFS.id) chiamano SOLO getVisitorType() e NON si accorgono di nulla.│
   │ Tutto il resto della v2 è ADDITIVO. Verificato con test di           │
   │ equivalenza bit-per-bit sui 64 casi della cascata (28 mag 2026).     │
   └──────────────────────────────────────────────────────────────────────┘

   ──────────────────────────────────────────────────────────────────────
   NOVITÀ v2.1.0 (28 mag): getVisitorType() ora consulta il modello di
   identità (observe) come ULTIMO livello, SOLO quando la cascata legacy
   restituirebbe 'unknown'. Così un RC/NC stabilito via segnale (es. order,
   login, quiz) diventa visibile a TUTTI i consumer che chiamano
   getVisitorType(), senza doverli migrare uno a uno a getProfile(). È
   ADDITIVO: nessun esito prodotto dai livelli 1-5 cambia (verificato:
   216 casi cascata identici). NB: la riconciliazione PER CONFIDENZA su
   tutti i livelli (far vincere il modello anche su segnali deboli come
   ?audience=) è RIMANDATA a dopo la chiusura dell'A/B test FS, per non
   falsarne i dati — vedi TODO[B] in getVisitorType e master §9.

   NOVITÀ v2.0.1 (28 mag): la persistenza su localStorage avviene solo se
   c'è davvero un'identità da ricordare (type != 'unknown'). Uno stato vuoto
   non viene più scritto in localStorage (resta solo in sessione). Verificato
   live in produzione. getVisitorType() invariato.

   NOVITÀ v2.0.0 — IDENTITÀ PERSISTENTE E ASCOLTO SEGNALI
   ──────────────────────────────────────────────────────────────────────
   La v1 era un classificatore "a lettura": leggeva cookie/URL/storage e
   restituiva NC/RC/unknown. La v2 aggiunge un MODELLO DI IDENTITÀ che:

   1. ASCOLTA segnali da qualsiasi touchpoint via SkonID.observe(signal, data),
      senza che SkonID sappia chi è il touchpoint. Ogni segnale porta un
      livello di CONFIDENZA (0-100). Un segnale aggiorna lo stato solo se la
      sua confidenza è >= a quella del segnale già memorizzato. Così un
      segnale forte (ordine) sovrascrive uno debole (url), ma non viceversa.

   2. PERSISTE lo stato:
      - SEMPRE in sessionStorage (funzionale, nessun consenso richiesto:
        serve a far funzionare la UX nella sessione corrente)
      - in localStorage (TTL 180gg) SOLO se il consenso "preferences" è
        accordato (Shopify Customer Privacy API). Senza consenso: niente
        persistenza cross-sessione, degradazione pulita identica alla v1.

   3. È ESTENDIBILE: per aggiungere un touchpoint futuro (sondaggio, login
      social, click email, ecc.) NON si tocca questo file. Il nuovo
      touchpoint chiama SkonID.observe('mio_segnale', {...}) dichiarando la
      confidenza nella tabella CONFIDENCE qui sotto. Tutto il resto è
      automatico. Vedi sezione "ESTENDERE" in fondo.

   ──────────────────────────────────────────────────────────────────────
   API PUBBLICA
   ──────────────────────────────────────────────────────────────────────
   - window.SkonID.getVisitorType()      → 'NC' | 'RC' | 'unknown'  — HA COMPRATO?
   - window.SkonID.hasCompletedQuiz()    → true | false  [v2.2.0] — HA UNA ROUTINE?
   - window.SkonID.markCustomer()        → registra acquisto accertato [v2.2.0]
   - window.SkonID.markNonCustomer()     → registra NON-acquisto accertato [v2.2.0]
   - window.SkonID.observe(signal, data) → registra un segnale, ritorna lo stato
   - window.SkonID.getProfile()          → oggetto stato completo (sola lettura)
   - window.SkonID.version               → '2.4.0'

   ──────────────────────────────────────────────────────────────────────
   CASCATA DI CLASSIFICAZIONE — getVisitorType() risponde a UNA domanda:
   «questo visitatore HA COMPRATO?»  (v2.2.0)
   ──────────────────────────────────────────────────────────────────────
   1.  skon_rc_verified === '1'                 → RC (conferma Worker, granitica)
   1b. cookie skon_customer === '1'             → RC (acquisto accertato, 365gg)
       ↑ scritto anche da _bootstrap quando Liquid dice customer.orders_count>0
   2.  cookie _shopify_customer_token presente  → RC (sessione Shopify autenticata)
   2c. cookie skon_nc_verified === '1'          → NC (NON-acquisto accertato, 365gg)
   ── il vecchio 2b (quiz completato → RC) è stato RIMOSSO in v2.2.0 ──
       «ha fatto il quiz» non è «ha comprato»: usa hasCompletedQuiz().
   3. URL ?audience=new|exi                     → NC | RC (segnale campagna Meta)
   4. skon_nc_declared === 'new'|'returning'    → NC | RC (autodichiarazione)
   5. memoria di sessione skon_visitor_type     → ritorna se già NC/RC
   default                                       → 'unknown'

   STORAGE:
   - sessionStorage.skon_visitor_type    = 'NC'|'RC'|'unknown'  (scritto a ogni classify)
   - sessionStorage.skon_nc_audience_raw = 'new'|'exi'          (traccia segnale URL)
   - sessionStorage.skon_nc_declared     = 'new'|'returning'    (scritto da fs-onboarding)
   - sessionStorage.skon_rc_verified     = '1'                  (post-conferma Worker RC)
   - sessionStorage.skon_identity_state  = {JSON stato}  [NUOVO v2, mirror di sessione]
   - localStorage.skon_identity_state    = {JSON stato}  [NUOVO v2, solo con consenso]

   DEPLOYMENT: caricare PRIMA di nc-experience.js, skon-fs-*.js e
   skon-protocol-widget.js in theme.liquid (è una dipendenza di tutti).
══════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var AUDIENCE_PARAM = 'audience';
  var STATE_KEY      = 'skon_identity_state';
  var STATE_SCHEMA   = 2;
  var PERSIST_TTL_MS = 180 * 24 * 60 * 60 * 1000; // 180 giorni

  // ──────────────────────────────────────────────────────────────────────
  //  TABELLA CONFIDENZA — l'unico punto da toccare per aggiungere segnali.
  //  Numero 0-100 = quanto è affidabile quel tipo di segnale sul "chi è".
  //  Più alto = più forte. Un segnale aggiorna lo stato solo se la sua
  //  confidenza è >= a quella attualmente memorizzata.
  // ──────────────────────────────────────────────────────────────────────
  var CONFIDENCE = {
    login:          100, // sessione Shopify autenticata / RC verificato dal Worker
    order:           90, // ordine completato (TYP / Worker vede l'ordine)
    quiz:            80, // quiz completato con email
    email_captured:  60, // [v2.3.0] email data volontariamente (modulo lead-gen NC
                         // dello step 2 del toast onboarding) — più forte di una
                         // semplice autodichiarazione, più debole di un acquisto
                         // o quiz verificato
    declared:        50, // autodichiarazione utente ("sono nuovo/di ritorno")
    utm:             30, // utm_source da canale CRM (klaviyo/magnews/sms/whatsapp...) → presunto RC
    url:             20  // ?audience=new|exi (segnale campagna Meta, debole)
    // PER AGGIUNGERE UN TOUCHPOINT FUTURO: aggiungere qui una voce, es.
    //   survey: 60,    // risposta a un sondaggio on-site
    //   emailclick: 40 // click su link in email CRM
    // e chiamare SkonID.observe('survey', { type:'RC', ... }) dal touchpoint.
  };

  // ── Helper interni (autosufficienti, nessuna dipendenza esterna) ──────
  function _getParam(name) {
    try { return new URLSearchParams(window.location.search).get(name); }
    catch (e) { return null; }
  }

  function _getCookie(name) {
    try {
      var m = document.cookie.match('(?:^|;)\\s*' + name + '=([^;]*)');
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }

  /* v2.2.0 — durata del segnale "ha comprato". Un cliente resta cliente per
     sempre, un cookie no. 365 giorni è il compromesso deciso con Matteo il
     10 lug 2026. Compromesso noto: su un computer condiviso, chi ha comprato
     una volta nega per un anno lo sconto di benvenuto al visitatore
     successivo. Il vecchio livello 2b faceva lo stesso danno in 60 giorni,
     e per giunta su chi non aveva comprato affatto. */
  var CUSTOMER_COOKIE      = 'skon_customer';
  var NONCUSTOMER_COOKIE   = 'skon_nc_verified';
  var CUSTOMER_COOKIE_DAYS = 365;
  var QUIZ_COOKIE          = 'skon_quiz_completed';

  function _setCookie(name, val, days) {
    try {
      document.cookie = name + '=' + encodeURIComponent(val)
        + '; max-age=' + (days * 86400) + '; path=/; SameSite=Lax';
    } catch (e) { /* cookie disabilitati: non è un errore fatale */ }
  }

  function _ss(key, val) {
    try {
      if (val === undefined) return sessionStorage.getItem(key);
      sessionStorage.setItem(key, val);
    } catch (e) { return null; }
  }

  function _ls(key, val) {
    try {
      if (val === undefined) return localStorage.getItem(key);
      localStorage.setItem(key, val);
    } catch (e) { return null; }
  }

  function _lsRemove(key) {
    try { localStorage.removeItem(key); } catch (e) {}
  }

  /* [v2.4.0] Analogo di _lsRemove ma per sessionStorage. Serve a
     _clearOnLogout() per ripulire le chiavi di sessione della cascata
     legacy: sessionStorage NON si svuota a ogni pagina, solo alla chiusura
     del tab/finestra, quindi senza questa pulizia esplicita sopravvivrebbe
     intatta alla navigazione di logout (stesso tab). */
  function _ssRemove(key) {
    try { sessionStorage.removeItem(key); } catch (e) {}
  }

  // ══════════════════════════════════════════════════════════════════════
  //  getVisitorType() — INVARIATO dalla v1.0.0
  //  NON MODIFICARE la logica di questa funzione: i consumer in produzione
  //  (nc-experience, skon-fs-test) ne dipendono. La cascata è identica
  //  bit-per-bit alla v1. Le novità v2 NON entrano qui.
  // ══════════════════════════════════════════════════════════════════════
  function getVisitorType() {

    // Livello 1 — RC verificato dal Worker (certezza granitica)
    if (_ss('skon_rc_verified') === '1') {
      _ss('skon_visitor_type', 'RC');
      return 'RC';
    }

    // Livello 1b [v2.2.0] — Acquisto accertato in passato (cookie 365gg).
    //   Scritto da markCustomer(), che viene invocata SOLO dopo che il Worker
    //   ha verificato l'email sugli ordini Shopify (verità assoluta).
    if (_getCookie(CUSTOMER_COOKIE) === '1') {
      _ss('skon_visitor_type', 'RC');
      return 'RC';
    }

    // Livello 2 — Sessione Shopify autenticata
    if (_getCookie('_shopify_customer_token')) {
      _ss('skon_visitor_type', 'RC');
      return 'RC';
    }

    /* Livello 2c [v2.2.0] — NC ACCERTATO: l'email è stata verificata sugli
       ordini Shopify e NON risulta alcun acquisto. Sta DOPO i livelli RC di
       proposito: se domani quella persona compra, markCustomer() scrive
       skon_customer e il livello 1b vince su questo. Un "no" di ieri non può
       sovrascrivere un "sì" di oggi. */
    if (_getCookie(NONCUSTOMER_COOKIE) === '1') {
      _ss('skon_visitor_type', 'NC');
      return 'NC';
    }

    /* ── RIMOSSO in v2.2.0: "Livello 2b — Quiz già completato in passato" ──
       Classificava RC chiunque avesse il cookie skon_quiz_completed.
       Era sbagliato e dannoso:
         • RC significa "ha comprato". Un lead che compila il quiz NON ha
           comprato: è un lead caldo, semmai il contrario di un cliente.
         • Non proteggeva alcun cliente: un cliente non loggato che non ha mai
           fatto il quiz era (ed è) classificato NC comunque.
         • Effetto misurato in produzione (9 lug 2026): dopo il quiz l'intera
           NC Experience si spegneva per 60 giorni — strip, nota prezzo,
           exit-intent, watchCartAdd — proprio quando il lead ha in mano un
           codice sconto personale e sta navigando il catalogo.
         • Rendeva irraggiungibile _renderPriceNote() in nc-experience.js:
           richiede skon_quiz_result, ma chi ce l'ha era classificato RC.

       "Ha comprato" e "ha fatto il quiz" sono DUE ASSI INDIPENDENTI.
       Il primo è getVisitorType(). Il secondo è hasCompletedQuiz(). */

    // Livello 3 — Parametro URL da campagna Meta
    //   (può sbagliare — non sovrascrive i livelli 1-2 perché vengono prima)
    var urlAudience = _getParam(AUDIENCE_PARAM);
    if (urlAudience === 'new') {
      _ss('skon_nc_audience_raw', 'new');
      _ss('skon_visitor_type', 'NC');
      return 'NC';
    }
    if (urlAudience === 'exi') {
      _ss('skon_nc_audience_raw', 'exi');
      _ss('skon_visitor_type', 'RC');
      return 'RC';
    }

    // Livello 4 — Autodichiarazione esplicita (dalla domanda di fs-onboarding)
    var declared = _ss('skon_nc_declared');
    if (declared === 'returning') { _ss('skon_visitor_type', 'RC'); return 'RC'; }
    if (declared === 'new')       { _ss('skon_visitor_type', 'NC'); return 'NC'; }

    // Livello 5 — Memoria di sessione (se già classificato in questa sessione)
    var existing = _ss('skon_visitor_type');
    if (existing === 'NC' || existing === 'RC') return existing;

    // Livello 6 [v2.1.0 — riconciliazione col modello, ADDITIVO] — se la cascata
    // legacy non ha trovato nulla (saremmo a 'unknown'), consultiamo il modello
    // di identità popolato da observe(): un RC/NC stabilito via segnale (order,
    // login propagato, quiz, declared) deve essere visibile ai consumer che
    // chiamano getVisitorType(). Questo NON cambia MAI un esito già prodotto dai
    // livelli 1-5: interviene SOLO dove oggi si restituirebbe 'unknown'.
    // Guard: _state è inizializzato a runtime (riga ~271); proteggiamo da
    // chiamate anticipate (es. durante il bootstrap, prima dell'init).
    //
    // TODO[B] (post-lancio, alla CHIUSURA dell'A/B test FS): valutare la
    // riconciliazione PER CONFIDENZA su TUTTI i livelli — cioè far vincere il
    // modello anche quando la cascata produce un valore DEBOLE (es. ?audience=new,
    // conf 20) ma il modello ha qualcosa di più forte (es. order, conf 90).
    // Oggi NON lo facciamo di proposito: cambiare il giudizio NC/RC durante un
    // A/B test in corso falserebbe i dati del test. La versione attuale (livello 6)
    // è puramente additiva sui casi 'unknown' e non altera nessun esito esistente.
    // Riferimento: master §4 / §9 pending "SkonID riconciliazione confidenza (B)".
    try {
      if (typeof _state !== 'undefined' && _state &&
          (_state.type === 'NC' || _state.type === 'RC') &&
          _state.confidence > 0) {
        // Allinea la chiave legacy di sessione per coerenza coi lettori grezzi
        _ss('skon_visitor_type', _state.type);
        return _state.type;
      }
    } catch (e) {}

    return 'unknown';
  }

  // ══════════════════════════════════════════════════════════════════════
  //  MODELLO DI IDENTITÀ v2 (additivo — non tocca getVisitorType)
  // ══════════════════════════════════════════════════════════════════════

  // ── Consenso: si può persistere su localStorage? ──────────────────────
  // Legge la Shopify Customer Privacy API (CMP-agnostica: vale con Consentmo
  // o qualsiasi altro banner). Categoria "preferences" = personalizzazione.
  // Fail-safe: in qualsiasi caso di dubbio/errore → false (NON persiste).
  function _canPersist() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.preferencesProcessingAllowed !== 'function') return false;
      return cp.preferencesProcessingAllowed() === true;
    } catch (e) { return false; }
  }

  // ── Stato di default ──────────────────────────────────────────────────
  function _emptyState() {
    return {
      v:                STATE_SCHEMA,
      type:             'unknown',
      confidence:       0,
      source:           null,
      profile:          null,
      discountAssigned: null,
      discountUsed:     null,
      hasOrdered:       false,
      updatedAt:        0,
      lastSeen:         0
    };
  }

  // ── Carica lo stato: prima localStorage (se valido e non scaduto), poi
  //    sessionStorage, poi vuoto. Non solleva mai. ────────────────────────
  function _loadState() {
    // 1) localStorage (persistente cross-sessione)
    try {
      var rawL = _ls(STATE_KEY);
      if (rawL) {
        var sL = JSON.parse(rawL);
        if (sL && sL.v === STATE_SCHEMA) {
          if (!sL.updatedAt || (Date.now() - sL.updatedAt) < PERSIST_TTL_MS) {
            return sL;
          }
          // scaduto → ripulisci
          _lsRemove(STATE_KEY);
        }
      }
    } catch (e) {}
    // 2) sessionStorage (mirror di sessione)
    try {
      var rawS = _ss(STATE_KEY);
      if (rawS) {
        var sS = JSON.parse(rawS);
        if (sS && sS.v === STATE_SCHEMA) return sS;
      }
    } catch (e) {}
    // 3) vuoto
    return _emptyState();
  }

  // ── Salva lo stato: sessionStorage SEMPRE; localStorage solo con consenso.
  //    Se il consenso non c'è, rimuove ogni eventuale residuo persistente. ─
  function _saveState(state) {
    var json;
    try { json = JSON.stringify(state); } catch (e) { return; }
    _ss(STATE_KEY, json);                 // sessione: sempre (funzionale)
    // Persistente (localStorage): solo se (a) c'è il consenso preferences E
    // (b) c'è davvero un'identità da ricordare. Uno stato 'unknown'/conf 0 non
    // vale la pena di essere persistito: sarebbe un cookie di profilazione che
    // memorizza il nulla. In quel caso (o senza consenso) rimuoviamo i residui.
    var worthPersisting = state && state.type !== 'unknown' && state.confidence > 0;
    if (_canPersist() && worthPersisting) {
      _ls(STATE_KEY, json);
    } else {
      _lsRemove(STATE_KEY);
    }
  }

  // Stato in memoria (single source nel runtime della pagina)
  var _state = _loadState();

  // ── Mappa un type espresso da un segnale a 'NC'|'RC'|'unknown' ─────────
  function _normType(t) {
    if (t === 'NC' || t === 'RC') return t;
    return 'unknown';
  }

  // ══════════════════════════════════════════════════════════════════════
  //  observe(signal, data) — la PORTA D'INGRESSO per i touchpoint.
  //  signal: chiave della tabella CONFIDENCE (es. 'order','quiz','utm'...).
  //  data:   { type?, profile?, discountAssigned?, discountUsed?, hasOrdered? }
  //  Regola: aggiorna lo stato solo se confidenza(signal) >= confidence
  //  attuale. I campi-dato (profile, codice...) si aggiornano se forniti e
  //  se il segnale è almeno forte quanto quello corrente (non si perde un
  //  profilo noto per colpa di un segnale debole successivo).
  //  Ritorna lo stato aggiornato (copia).
  // ══════════════════════════════════════════════════════════════════════
  function observe(signal, data) {
    data = data || {};
    var conf = CONFIDENCE[signal];
    if (typeof conf !== 'number') {
      // segnale sconosciuto: ignora in sicurezza (non rompe)
      return getProfile();
    }

    var changed = false;
    var incomingType = _normType(data.type);

    // 1) type/confidence: aggiorna solo se il nuovo segnale è >= dell'attuale
    if (incomingType !== 'unknown' && conf >= _state.confidence) {
      if (_state.type !== incomingType || conf > _state.confidence) {
        _state.type       = incomingType;
        _state.confidence = conf;
        _state.source     = signal;
        changed = true;
      }
    }

    // 2) campi-dato: si arricchiscono se forniti e se il segnale non è più
    //    debole di quello che ha prodotto lo stato attuale (evita che un
    //    segnale debole cancelli dati raccolti da uno forte). I dati nuovi
    //    e non nulli sono comunque preziosi: li accettiamo se conf >= soglia.
    if (conf >= Math.min(_state.confidence, CONFIDENCE.declared)) {
      if (data.profile != null && data.profile !== _state.profile) {
        _state.profile = data.profile; changed = true;
      }
      if (data.discountAssigned != null && data.discountAssigned !== _state.discountAssigned) {
        _state.discountAssigned = data.discountAssigned; changed = true;
      }
      if (data.discountUsed != null && data.discountUsed !== _state.discountUsed) {
        _state.discountUsed = data.discountUsed; changed = true;
      }
      if (data.hasOrdered === true && _state.hasOrdered !== true) {
        _state.hasOrdered = true; changed = true;
      }
    }

    _state.lastSeen = Date.now();
    if (changed) {
      _state.updatedAt = Date.now();
      // Allinea la chiave legacy di sessione, così getVisitorType (livello 5)
      // e i consumer che leggono skon_visitor_type restano coerenti.
      if (_state.type === 'NC' || _state.type === 'RC') {
        _ss('skon_visitor_type', _state.type);
      }
    }
    _saveState(_state);
    return getProfile();
  }

  // ── getProfile() — stato completo in sola lettura (copia difensiva) ────
  function getProfile() {
    return {
      v:                _state.v,
      type:             _state.type,
      confidence:       _state.confidence,
      source:           _state.source,
      profile:          _state.profile,
      discountAssigned: _state.discountAssigned,
      discountUsed:     _state.discountUsed,
      hasOrdered:       _state.hasOrdered,
      updatedAt:        _state.updatedAt,
      lastSeen:         _state.lastSeen
    };
  }

  // ── Pulizia identità al logout [v2.4.0] ────────────────────────────────
  // Vedi changelog in testa al file per il razionale completo. Chiamata
  // SOLO da _bootstrap(), quando verità server-side (SKON_CUSTOMER.loggedIn
  // === false) e memoria di sessione (skon_rc_verified ancora '1') sono in
  // contraddizione: prova che QUESTO tab era autenticato e ora non lo è più.
  function _clearOnLogout() {
    // 1) Modello v2: oggetto in memoria + entrambi gli storage.
    _state = _emptyState();
    _ssRemove(STATE_KEY);
    _lsRemove(STATE_KEY);

    // 2) Memoria di sessione della cascata legacy: senza questa pulizia,
    //    getVisitorType() riconoscerebbe comunque RC il prossimo
    //    visitatore sullo stesso tab (Livelli 1, 4, 5), vanificando la
    //    pulizia del solo modello v2 fatta al punto 1.
    _ssRemove('skon_rc_verified');
    _ssRemove('skon_visitor_type');
    _ssRemove('skon_nc_declared');
    _ssRemove('skon_nc_audience_raw');

    // 3) skon_vid: non è di proprietà di questo file (lo scrivono
    //    skon-fs-onboarding.js e skon-commerce-events.js), ma Matteo ha
    //    chiesto esplicitamente che un logout lo invalidi. Lo cancelliamo
    //    come semplice cookie — stesso nome/path/SameSite usato da
    //    entrambi quei file — senza chiamare né duplicare la loro logica
    //    di generazione: alla prossima _ensureVisitorId() di uno dei due,
    //    non trovando il cookie, ne creano uno nuovo da soli.
    _setCookie('skon_vid', '', 0);

    /* NON tocchiamo qui skon_customer / skon_nc_verified (cookie 365gg):
       vedi changelog v2.4.0 in testa al file per il perché — è un
       perimetro deliberatamente fuori da questa modifica. */
  }

  // ── Bootstrap: alla prima esecuzione, alimenta il modello v2 con ciò che
  //    la cascata legacy già sa, SENZA alterare la cascata stessa. Così lo
  //    stato persistente parte coerente con la classificazione storica. ───
  function _bootstrap() {
    /* [v2.4.0] — rilevazione logout, PRIMA di ogni altra cosa (anche prima
       della certificazione server-side subito sotto): se per QUESTO tab
       risultava autenticato (skon_rc_verified sessionStorage ancora '1')
       e ora SKON_CUSTOMER dice il contrario, ripulisci tutto. Deve stare
       per prima: altrimenti un _state/sessionStorage residuo da RC
       potrebbe influenzare le righe successive di questa stessa funzione
       (es. getVisitorType() più in basso). Guardia sicura: skon_rc_verified
       ha un solo scrittore in questo file (markCustomer()), quindi nessun
       falso positivo per chi non è mai stato loggato. */
    try {
      var CLogout = window.SKON_CUSTOMER;
      if (CLogout && CLogout.loggedIn === false && _ss('skon_rc_verified') === '1') {
        _clearOnLogout();
      }
    } catch (e) {}

    /* v2.2.0 — CERTIFICAZIONE SERVER-SIDE, prima di ogni altra cosa.
       `window.SKON_CUSTOMER` è iniettata da Liquid (theme.liquid e
       quiz-main.liquid) dall'oggetto `customer` di Shopify. È verità di
       origine, non un'euristica, e non costa una chiamata di rete.

       Perché non dentro getVisitorType(): markCustomer() chiama observe(), che
       aggiorna il modello; e _bootstrap chiama getVisitorType(). Certificare
       dentro la cascata sarebbe una ricorsione in attesa di accadere. Qui gira
       una volta per pagina, prima che qualcuno chieda qualcosa.

       ordersCount === 0 → NC accertato: si può creare un account senza mai
       comprare. "Loggato" non è "ha comprato". */
    try {
      var C = window.SKON_CUSTOMER;
      if (C && C.loggedIn) {
        if (C.ordersCount > 0) markCustomer();
        else                   markNonCustomer();
      }
    } catch (e) { /* Liquid non ha iniettato nulla: si prosegue con la cascata */ }

    // Segnali deboli da URL/utm (registrati nel modello, non cambiano la cascata)
    var urlAudience = _getParam(AUDIENCE_PARAM);
    if (urlAudience === 'new') observe('url', { type: 'NC' });
    else if (urlAudience === 'exi') observe('url', { type: 'RC' });

    var utm = _getParam('utm_source');
    if (utm) {
      var u = String(utm).toLowerCase();
      // canali tipicamente CRM → presunto RC (segnale debole)
      if (/klaviyo|magnews|textyess|whatsapp|sms|email/.test(u)) {
        observe('utm', { type: 'RC' });
      }
    }

    // Segnale forte: classificazione legacy corrente.
    // Reidrata il modello col verdetto della cascata, mappandolo a confidenza.
    var legacy = getVisitorType();
    if (legacy === 'RC' || legacy === 'NC') {
      /* v2.2.0 — La fonte "forte" è l'ACQUISTO ACCERTATO: conferma del Worker,
         cookie skon_customer, o sessione Shopify autenticata. Prima il cookie
         del quiz era considerato una fonte forte del verdetto: ora il quiz non
         produce più alcun verdetto su NC/RC, quindi attribuirgliene uno
         inquinerebbe la confidenza del modello con un segnale che non esiste. */
      if (_ss('skon_rc_verified') === '1'
          || _getCookie(CUSTOMER_COOKIE) === '1'
          || _getCookie('_shopify_customer_token')) {
        observe('login', { type: legacy });
      } else {
        var declared = _ss('skon_nc_declared');
        if (declared === 'new' || declared === 'returning') observe('declared', { type: legacy });
        // altrimenti era già coperto da url/utm sopra
      }
    }
  }

  // ── Listener consenso: se l'utente concede "preferences" DOPO il load,
  //    promuovi lo stato di sessione a persistente (flush su localStorage). ─
  try {
    document.addEventListener('visitorConsentCollected', function () {
      if (_canPersist()) _saveState(_state);
      else _lsRemove(STATE_KEY);
    });
  } catch (e) {}

  // ── Esposizione API pubblica ──────────────────────────────────────────
  // Idempotente: se un SkonID è già presente (es. v1 caricato altrove), NON
  // lo sovrascrive — ma se è la v1, la v2 è un superset compatibile, quindi
  // preferiamo la v2 se la sua versione è maggiore.
  /* ── v2.2.0 — I DUE ASSI ──────────────────────────────────────────────────
     getVisitorType()  → HA COMPRATO?      NC | RC | unknown
     hasCompletedQuiz() → HA UNA ROUTINE?  true | false
     Sono indipendenti. Un lead post-quiz è NC e ha una routine. Un cliente che
     non ha mai fatto il quiz è RC e non ne ha. Prima del 10 lug 2026 i due assi
     erano collassati in una variabile sola, e il secondo sovrascriveva il primo. */
  function hasCompletedQuiz() {
    return _getCookie(QUIZ_COOKIE) === '1';
  }

  /* Registra l'acquisto accertato. Da chiamare SOLO dopo che il Worker ha
     verificato l'email sugli ordini Shopify (`/check-customer` → isCustomer).
     Esiste perché la regola viva in UN posto: prima della v2.2.0 la stessa
     cascata era copiata in skon-identity.js, nc-experience.js e skon-fs-test.js,
     e correggerne una sola avrebbe lasciato le altre due a mentire. */
  function markCustomer() {
    _setCookie(CUSTOMER_COOKIE, '1', CUSTOMER_COOKIE_DAYS);
    _ss('skon_rc_verified', '1');
    _ss('skon_visitor_type', 'RC');
    /* Alimenta anche il modello: senza, getProfile() resterebbe cieco su una
       certezza server-side. Segnale 'order' (conf. 90): il Worker ha visto un
       ordine reale su Shopify. 'login' (100) resta riservato alla sessione
       autenticata. Consumatori come skon-fs-onboarding leggono getProfile(),
       non getVisitorType(): senza questa riga tornerebbero a fare domande a
       cui abbiamo già la risposta. */
    try { observe('order', { type: 'RC' }); } catch (e) {}
  }

  /* Registra il NON-acquisto accertato. Anche questa è una certificazione, non
     un'euristica: il Worker ha cercato l'email negli ordini Shopify e non ha
     trovato nulla. Nessuno deve uscire dal quiz senza classificazione: da quel
     momento abbiamo l'email, quindi abbiamo la verità.
     Non è simmetrica a markCustomer(): un "non ha comprato" è vero OGGI, e il
     livello 1b (skon_customer) lo scavalca appena la persona compra. */
  function markNonCustomer() {
    _setCookie(NONCUSTOMER_COOKIE, '1', CUSTOMER_COOKIE_DAYS);
    _ss('skon_visitor_type', 'NC');
    /* Segnale 'quiz' (conf. 80): "quiz completato con email", che è
       esattamente il nostro caso — con in più la verifica sugli ordini. */
    try { observe('quiz', { type: 'NC' }); } catch (e) {}
  }

  if (!window.SkonID || _verLt(window.SkonID.version, '2.4.0')) {
    window.SkonID = {
      version:          '2.4.0',
      getVisitorType:   getVisitorType,
      hasCompletedQuiz: hasCompletedQuiz,
      markCustomer:     markCustomer,
      markNonCustomer:  markNonCustomer,
      observe:          observe,
      getProfile:       getProfile
    };
    _bootstrap();
  }

  // Confronto semver minimale (a < b ?). Tollera undefined.
  function _verLt(a, b) {
    if (!a) return true;
    var pa = String(a).split('.').map(Number);
    var pb = String(b).split('.').map(Number);
    for (var i = 0; i < 3; i++) {
      var x = pa[i] || 0, y = pb[i] || 0;
      if (x < y) return true;
      if (x > y) return false;
    }
    return false;
  }

})();

/* ══════════════════════════════════════════════════════════════════════
   ESTENDERE — aggiungere un nuovo touchpoint di conferma segnali
   ──────────────────────────────────────────────────────────────────────
   NON si modifica la logica di questo file. Due passi:

   1. Aggiungere una voce in CONFIDENCE (in cima) con un valore 0-100 che
      riflette quanto è affidabile quel segnale, posizionandolo nella scala:
         login 100 > order 90 > quiz 80 > declared 50 > utm 30 > url 20

   2. Dal nuovo touchpoint, chiamare:
         window.SkonID && window.SkonID.observe('mio_segnale', {
           type: 'RC',                 // opzionale: 'NC'|'RC'
           profile: 'C',               // opzionale
           discountAssigned: 'SK-...', // opzionale
           discountUsed: false,        // opzionale
           hasOrdered: true            // opzionale
         });

   SkonID applica da solo la regola di priorità per confidenza, aggiorna lo
   stato, allinea la chiave legacy di sessione e persiste (se c'è consenso).
   getVisitorType() resta la fonte per i consumer legacy; getProfile() per
   chi vuole lo stato ricco.
══════════════════════════════════════════════════════════════════════ */
